import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function IdealWeightCalculator() {
  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Health", url: "https://calclab.app/health" },
      { name: "Ideal Weight Calculator", url: "https://calclab.app/calculator/ideal-weight" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Ideal Weight Calculator",
      "Calculate ideal weight based on height, gender, and body frame. Free online health calculator.",
      "https://calclab.app/calculator/ideal-weight",
      lang
    )
  ]);
  const [height, setHeight] = useState("");
  const [gender, setGender] = useState("male");
  const [frame, setFrame] = useState("medium");
  const [results, setResults] = useState<any>(null);

  const calculateIdealWeight = () => {
    const h = parseFloat(height);
    if (!h || h <= 0) return;
    
    const heightInInches = h / 2.54;
    const baseHeight = 60;
    
    let idealWeight: number;
    if (gender === "male") {
      idealWeight = 50 + 2.3 * (heightInInches - baseHeight);
    } else {
      idealWeight = 45.5 + 2.3 * (heightInInches - baseHeight);
    }
    
    let adjustment = 1;
    if (frame === "small") adjustment = 0.9;
    if (frame === "large") adjustment = 1.1;
    
    idealWeight *= adjustment;
    
    const min = idealWeight * 0.95;
    const max = idealWeight * 1.05;
    
    setResults({
      ideal: Math.round(idealWeight),
      min: Math.round(min),
      max: Math.round(max)
    });
  };

  return (
    <PageLayout calculatorName="Ideal Weight Calculator" calculatorPath="/calculator/ideal-weight">
      <div className="grid gap-8 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Calculate Ideal Weight</CardTitle>
            <CardDescription>Based on height and body frame</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Gender</Label>
              <Select value={gender} onValueChange={setGender}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Height (cm)</Label>
              <Input type="number" value={height} onChange={(e) => setHeight(e.target.value)} placeholder="175" />
            </div>
            <div>
              <Label>Body Frame</Label>
              <Select value={frame} onValueChange={setFrame}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="small">Small</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="large">Large</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={calculateIdealWeight} className="w-full">Calculate</Button>
          </CardContent>
        </Card>

        {results && (
          <>
            <Card>
              <CardHeader>
                <CardTitle>Your Ideal Weight</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-center">
                  <div className="text-5xl font-bold text-primary mb-2">{results.ideal} kg</div>
                  <div className="text-lg text-muted-foreground">Ideal Weight</div>
                </div>
                <div className="border-t pt-4">
                  <div className="flex justify-between text-lg">
                    <span>Healthy Range:</span>
                    <span className="font-semibold">{results.min} - {results.max} kg</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="md:col-span-2">
              <CalculatorSuperCalcCTA config={calculatorCTAs["ideal-weight"]} trigger={results.ideal} />
            </div>
          </>
        )}
      </div>
    </PageLayout>
  );
}
